import hoistNonReactStatics from 'hoist-non-react-statics';
import type { Component, ComponentClass, Ref } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { findHighest } from 'utils/dom-helpers';
import { notNull } from 'utils/typesafe';

const eventTypes: ReadonlyArray<keyof DocumentEventMap> = ['click', 'touchstart'];
const defaultIgnoredClasses = ['ignore-react-onclickoutside'] as const;

function isRefObject<T>(maybeRef: unknown): maybeRef is React.RefObject<T> {
  return typeof maybeRef === 'object' && maybeRef !== null && 'current' in maybeRef;
}

export interface ClickOutsideProps<T extends HTMLElement> {
  clickOutsideRef: Ref<T>;
  outsideClickIgnoreClass?: string;
}

export interface ClickOutsideComponent {
  handleClickOutside: () => void;
}

/**
 * Tracks clicks and invokes a callback when the user clicks outside of the ref element.
 * @param handlerOrRef A callback to invoke when the user clicks outside of the ref element,
 * or a ref to a component class instance with a matching handler.
 * @returns Ref to the DOM element that should be watched for outside clicks.
 */
export function useOnClickOutside<T extends HTMLElement>(
  handlerOrRef: (() => void) | React.RefObject<ClickOutsideComponent>,
  outsideClickIgnoreClass?: string
): Ref<T> {
  const [ref, setRef] = useState<T | null>(null);

  const ignoredClasses = useMemo(() => {
    return [...defaultIgnoredClasses, outsideClickIgnoreClass].filter(notNull);
  }, [outsideClickIgnoreClass]);

  useEffect(() => {
    if (!ref) {
      return;
    }

    const handler = isRefObject(handlerOrRef)
      ? handlerOrRef.current?.handleClickOutside
      : handlerOrRef;

    if (!handler) {
      return;
    }

    const listener = (event: Event) => {
      const target = event.target as Node;
      if (findHighest(target, ref, ignoredClasses) !== document) {
        return;
      }

      handler();
    };

    for (const type of eventTypes) {
      document.addEventListener(type, listener, { passive: type.startsWith('touch') });
    }

    return () => {
      for (const type of eventTypes) {
        document.removeEventListener(type, listener);
      }
    };
  }, [handlerOrRef, ignoredClasses, ref]);

  return setRef;
}

/**
 * Tracks clicks and invokes a callback when the user clicks outside of the element.
 * @example
 * interface Props extends ClickOutsideProps {
 *   // own props
 * }
 *
 * class FancyWidget
 *   extends React.Component<Props>
 *   // Implement this interface by adding `handleClickOutside`. You don't _have_ to include this
 *   // interface as long as the matching callback is in place, but it makes it more explicit
 *   implements ClickOutsideComponent
 * {
 *   // Callback when clicking outside
 *   handleClickOutside = () => {  }
 *
 *   render() {
 *     // Apply the injected ref to the click outside element
 *     return (
 *       <div ref={this.props.clickOutsideRef}>
 *         (content here)
 *       </div>
 *     )
 *   }
 * }
 */
export function withOnClickOutside<T extends HTMLElement, P extends ClickOutsideProps<T>, S>(
  Component: ComponentClass<P, S> & { new (...args: Array<any>): ClickOutsideComponent }
) {
  type PropsWithoutInjected = Omit<P, 'clickOutsideRef'>;
  const C: React.FC<PropsWithoutInjected> = ({ outsideClickIgnoreClass, ...props }) => {
    const instanceRef = useRef<Component<P, S> & ClickOutsideComponent>(null);
    const clickOutsideRef = useOnClickOutside(instanceRef, outsideClickIgnoreClass);
    return <Component ref={instanceRef} {...(props as P)} clickOutsideRef={clickOutsideRef} />;
  };

  C.displayName = `withOnClickOutside(${Component.displayName || Component.name})`;
  (C as any).WrappedComponent = Component;

  return hoistNonReactStatics(C, Component);
}
